/**
 * Add a grid (screen 09). Same league, same type, same members, same
 * settings, same season. Classic picks one more game (Today, This week, Pick
 * a date); Shared picks another run of games with the preset flow. "This
 * grid" restates what the season's new-grid rules do with squares and
 * numbers. Publish now and Notify members start on. The request carries one
 * transaction id per open sheet, so a double tap adds one grid.
 *
 * ISSUE-1282 adds a second kind, a custom game with no feed, after v1's
 * custom_pool_match_form.dart: a name (blank lets the server pick "Custom
 * game N") and the two axis labels. Its grid starts empty, locks when an
 * admin locks it and takes winners by hand. Classic seasons only: a Shared
 * season's Game tab lists the open grid's games, not its grids, so the
 * switch is not offered there and the server refuses it too.
 */
import {
  useSquaresCustomGridAdd,
  useSquaresGridAdd,
  useSquaresPresetsGet,
  useSquaresPreviewGames,
} from "@sportsmet/contest-sdk/private";
import { Button, Dialog, Input, SegmentedControl, Switch } from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import {
  confirmSquaresDiscard,
  runSquaresMutation,
  useRefreshSquaresPoolLine,
} from "./squaresActions";
import { SquaresChipGroup } from "./SquaresChipGroup";
import {
  SQUARES_ADD_GRID_CANCEL,
  SQUARES_ADD_GRID_DESCRIPTION,
  SQUARES_ADD_GRID_MEMBERS,
  SQUARES_ADD_GRID_MEMBERS_VALUE,
  SQUARES_ADD_GRID_NOTIFY,
  SQUARES_ADD_GRID_NUMBERS,
  SQUARES_ADD_GRID_NUMBERS_KEEP,
  SQUARES_ADD_GRID_NUMBERS_REDRAW,
  SQUARES_ADD_GRID_PUBLISH_NOW,
  SQUARES_ADD_GRID_PUBLISH_NOW_DESCRIPTION,
  SQUARES_ADD_GRID_SQUARES,
  SQUARES_ADD_GRID_SQUARES_CARRY,
  SQUARES_ADD_GRID_SQUARES_EMPTY,
  SQUARES_ADD_GRID_SUBMIT,
  SQUARES_ADD_GRID_THIS_GRID,
  SQUARES_ADD_GRID_TITLE,
  SQUARES_ADD_GRID_SOURCE_CUSTOM,
  SQUARES_ADD_GRID_SOURCE_LABEL,
  SQUARES_ADD_GRID_SOURCE_SCHEDULE,
  SQUARES_CUSTOM_GRID_AXIS_MAX_LENGTH,
  SQUARES_CUSTOM_GRID_DESCRIPTION,
  SQUARES_CUSTOM_GRID_NAME_HINT,
  SQUARES_CUSTOM_GRID_NAME_LABEL,
  SQUARES_CUSTOM_GRID_NAME_MAX_LENGTH,
  SQUARES_CUSTOM_GRID_NAME_PLACEHOLDER,
  SQUARES_CUSTOM_GRID_NUMBERS_VALUE,
  SQUARES_CUSTOM_GRID_SIDE_LABEL,
  SQUARES_CUSTOM_GRID_SIDE_PLACEHOLDER,
  SQUARES_CUSTOM_GRID_TOP_LABEL,
  SQUARES_CUSTOM_GRID_TOP_PLACEHOLDER,
  SQUARES_CUSTOM_GRID_WINNERS,
  SQUARES_CUSTOM_GRID_WINNERS_VALUE,
  SQUARES_SETUP_GAMES_HEADING,
} from "./squaresCopy";
import { isSquaresGameRuleReady } from "./squaresCreateSetupDraft";
import { squaresTransactionId } from "./squaresModel";
import { SquaresOneGamePicker } from "./SquaresOneGamePicker";
import { SquaresPresetPreview } from "./SquaresPresetPreview";
import type {
  typeSquaresGameRule,
  typeSquaresGridSummary,
  typeSquaresPresetKey,
  typeSquaresSeason,
} from "./squaresTypes";
import type { typeSquaresTeam } from "./useSquaresLeagueTeams";

type typeGridSource = "schedule" | "custom";

export type typeSquaresAddGridSheetProps = {
  open: boolean;
  onClose: () => void;
  poolId: string;
  season: typeSquaresSeason;
  grids: readonly typeSquaresGridSummary[];
  teams: readonly typeSquaresTeam[];
  teamsById: ReadonlyMap<string, typeSquaresTeam>;
};

export const SquaresSummaryRow = (props: {
  label: string;
  value: string;
}): ReactNode => (
  <div className="flex items-start justify-between gap-3 py-2">
    <dt className="text-caption text-ink-secondary">{props.label}</dt>
    <dd className="text-right text-body text-ink">{props.value}</dd>
  </div>
);

export const SquaresAddGridSheet = (
  props: typeSquaresAddGridSheetProps,
): ReactNode => {
  const { season } = props;
  const isClassic = season.gridType === "classic";
  const [eventId, setEventId] = useState<string | null>(null);
  const [rule, setRule] = useState<typeSquaresGameRule | null>(null);
  const refreshPoolLine = useRefreshSquaresPoolLine(props.poolId);
  const [doPublishNow, setPublishNow] = useState(true);
  const [doNotifyMembers, setNotifyMembers] = useState(true);
  const [transactionId, setTransactionId] = useState(squaresTransactionId);
  const [isBusy, setBusy] = useState(false);
  const [source, setSource] = useState<typeGridSource>("schedule");
  const [customTitle, setCustomTitle] = useState("");
  const [topLabel, setTopLabel] = useState("");
  const [sideLabel, setSideLabel] = useState("");
  const isCustom = isClassic && source === "custom";

  useEffect(() => {
    if (!props.open) return;
    setEventId(null);
    setRule(null);
    setSource("schedule");
    setCustomTitle("");
    setTopLabel("");
    setSideLabel("");
    setPublishNow(true);
    setNotifyMembers(true);
    setTransactionId(squaresTransactionId());
  }, [props.open]);

  const presetsQuery = useSquaresPresetsGet(
    { leagueKey: season.leagueKey },
    {
      enabled: props.open && !isClassic && !isCustom,
      staleTime: 5 * 60 * 1000,
    },
  );
  const presets = presetsQuery.data?.data ?? null;
  const isRuleReady = rule !== null && isSquaresGameRuleReady(rule);
  const previewQuery = useSquaresPreviewGames(
    {
      leagueKey: season.leagueKey,
      gridType: season.gridType,
      gameRule: rule ?? { presetKey: "allGames", excludedEventIds: [] },
    },
    {
      enabled: props.open && !isClassic && !isCustom && isRuleReady,
      placeholderData: (previous) => previous,
    },
  );
  const preview = isRuleReady ? (previewQuery.data?.data ?? null) : null;
  const gridAdd = useSquaresGridAdd();
  const customGridAdd = useSquaresCustomGridAdd();

  const isReady = isCustom
    ? topLabel.trim() !== "" && sideLabel.trim() !== ""
    : isClassic
      ? eventId !== null
      : isRuleReady &&
        preview !== null &&
        !previewQuery.isPlaceholderData &&
        preview.canCreate;
  const isDirty = isCustom
    ? customTitle.trim() !== "" ||
      topLabel.trim() !== "" ||
      sideLabel.trim() !== ""
    : eventId !== null || rule !== null;
  const selectedPreset =
    rule === null || presets === null
      ? null
      : (presets.gamePresets.find(
          (preset) => preset.presetKey === rule.presetKey,
        ) ?? null);

  async function submit(): Promise<void> {
    if (!isReady) return;
    setBusy(true);
    const shared = {
      poolId: props.poolId,
      doPublishNow,
      doNotifyMembers: doPublishNow && doNotifyMembers,
      transactionId,
    };
    const result = isCustom
      ? await runSquaresMutation(
          () =>
            customGridAdd.mutateAsync({
              ...shared,
              title: customTitle.trim() === "" ? null : customTitle,
              topLabel,
              sideLabel,
            }),
          refreshPoolLine,
        )
      : await runSquaresMutation(
          () =>
            gridAdd.mutateAsync({
              ...shared,
              ...(isClassic ? { eventId } : { gameRule: rule }),
            }),
          refreshPoolLine,
        );
    setBusy(false);
    if (result !== null) props.onClose();
  }

  return (
    <Dialog
      open={props.open}
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
      title={SQUARES_ADD_GRID_TITLE}
      description={
        isCustom
          ? SQUARES_CUSTOM_GRID_DESCRIPTION
          : SQUARES_ADD_GRID_DESCRIPTION
      }
      autoFocus={false}
      isDirty={isDirty && !isBusy}
      onRequestClose={() => {
        void (async () => {
          if (await confirmSquaresDiscard("grid")) props.onClose();
        })();
      }}
      footer={
        <>
          <Button variant="secondary" onClick={props.onClose}>
            {SQUARES_ADD_GRID_CANCEL}
          </Button>
          <Button
            disabled={!isReady}
            loading={isBusy}
            data-testid="squares-add-grid-submit"
            onClick={() => {
              void submit();
            }}
          >
            {SQUARES_ADD_GRID_SUBMIT}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4" data-testid="squares-add-grid-sheet">
        {isClassic ? (
          <SegmentedControl
            label={SQUARES_ADD_GRID_SOURCE_LABEL}
            testId="squares-add-grid-source"
            fullWidth
            options={[
              { value: "schedule", label: SQUARES_ADD_GRID_SOURCE_SCHEDULE },
              { value: "custom", label: SQUARES_ADD_GRID_SOURCE_CUSTOM },
            ]}
            value={source}
            onValueChange={(next) => {
              const nextSource = next === "custom" ? "custom" : "schedule";
              if (nextSource === source) return;
              setSource(nextSource);
              // ISSUE-1282. The two sources post to different endpoints, so a
              // key used for one must never replay as the other: a schedule
              // add that went through behind a failed answer would otherwise
              // come back as the "new" custom grid.
              setTransactionId(squaresTransactionId());
            }}
          />
        ) : null}
        {isCustom ? (
          <section
            className="flex flex-col gap-3"
            data-testid="squares-custom-grid-fields"
          >
            <Input
              label={SQUARES_CUSTOM_GRID_NAME_LABEL}
              hint={SQUARES_CUSTOM_GRID_NAME_HINT}
              placeholder={SQUARES_CUSTOM_GRID_NAME_PLACEHOLDER}
              maxLength={SQUARES_CUSTOM_GRID_NAME_MAX_LENGTH}
              value={customTitle}
              data-testid="squares-custom-grid-name"
              onChange={(event) => {
                setCustomTitle(event.target.value);
              }}
            />
            <Input
              label={SQUARES_CUSTOM_GRID_TOP_LABEL}
              placeholder={SQUARES_CUSTOM_GRID_TOP_PLACEHOLDER}
              maxLength={SQUARES_CUSTOM_GRID_AXIS_MAX_LENGTH}
              value={topLabel}
              data-testid="squares-custom-grid-top"
              onChange={(event) => {
                setTopLabel(event.target.value);
              }}
            />
            <Input
              label={SQUARES_CUSTOM_GRID_SIDE_LABEL}
              placeholder={SQUARES_CUSTOM_GRID_SIDE_PLACEHOLDER}
              maxLength={SQUARES_CUSTOM_GRID_AXIS_MAX_LENGTH}
              value={sideLabel}
              data-testid="squares-custom-grid-side"
              onChange={(event) => {
                setSideLabel(event.target.value);
              }}
            />
          </section>
        ) : (
          <section className="flex flex-col gap-3">
            <h3 className="text-body-strong text-ink">
              {SQUARES_SETUP_GAMES_HEADING}
            </h3>
            {isClassic ? (
              <SquaresOneGamePicker
                leagueKey={season.leagueKey}
                gridType={season.gridType}
                selectedEventId={eventId}
                onSelect={setEventId}
                teamsById={props.teamsById}
                hiddenEventIds={props.grids
                  .map((grid) => grid.eventId)
                  .filter(
                    (id): id is string => id !== null && id !== undefined,
                  )}
              />
            ) : presets === null ? null : (
              <>
                <SquaresChipGroup
                  mode="single"
                  label={SQUARES_SETUP_GAMES_HEADING}
                  testId="squares-add-grid-presets"
                  value={rule?.presetKey ?? null}
                  options={presets.gamePresets
                    .filter((preset) => preset.gridTypes.includes("shared"))
                    .map((preset) => ({
                      value: preset.presetKey,
                      label: preset.label,
                    }))}
                  onChange={(presetKey) => {
                    setRule({
                      presetKey: presetKey as typeSquaresPresetKey,
                      excludedEventIds: [],
                    });
                  }}
                />
                {rule === null || selectedPreset === null ? null : (
                  <SquaresPresetPreview
                    leagueKey={season.leagueKey}
                    preset={selectedPreset}
                    gameRule={rule}
                    preview={preview}
                    isPreviewLoading={isRuleReady && previewQuery.isLoading}
                    teams={props.teams}
                    teamsById={props.teamsById}
                    onChangeRule={setRule}
                    onToggleGame={(toggled) => {
                      const excluded = new Set(rule.excludedEventIds);
                      if (excluded.has(toggled)) excluded.delete(toggled);
                      else excluded.add(toggled);
                      setRule({ ...rule, excludedEventIds: [...excluded] });
                    }}
                  />
                )}
              </>
            )}
          </section>
        )}
        <section
          className="flex flex-col gap-1"
          data-testid="squares-add-grid-this-grid"
        >
          <h3 className="text-body-strong text-ink">
            {SQUARES_ADD_GRID_THIS_GRID}
          </h3>
          <dl className="flex flex-col divide-y divide-hairline">
            <SquaresSummaryRow
              label={SQUARES_ADD_GRID_MEMBERS}
              value={SQUARES_ADD_GRID_MEMBERS_VALUE}
            />
            <SquaresSummaryRow
              label={SQUARES_ADD_GRID_SQUARES}
              value={
                season.newGrid.carrySquares && !isCustom
                  ? SQUARES_ADD_GRID_SQUARES_CARRY
                  : SQUARES_ADD_GRID_SQUARES_EMPTY
              }
            />
            <SquaresSummaryRow
              label={SQUARES_ADD_GRID_NUMBERS}
              value={
                isCustom
                  ? SQUARES_CUSTOM_GRID_NUMBERS_VALUE
                  : season.newGrid.redrawNumbers
                    ? SQUARES_ADD_GRID_NUMBERS_REDRAW
                    : SQUARES_ADD_GRID_NUMBERS_KEEP
              }
            />
            {isCustom ? (
              <SquaresSummaryRow
                label={SQUARES_CUSTOM_GRID_WINNERS}
                value={SQUARES_CUSTOM_GRID_WINNERS_VALUE}
              />
            ) : null}
          </dl>
        </section>
        <Switch
          label={SQUARES_ADD_GRID_PUBLISH_NOW}
          description={SQUARES_ADD_GRID_PUBLISH_NOW_DESCRIPTION}
          checked={doPublishNow}
          onCheckedChange={setPublishNow}
        />
        <Switch
          label={SQUARES_ADD_GRID_NOTIFY}
          checked={doPublishNow && doNotifyMembers}
          disabled={!doPublishNow}
          onCheckedChange={setNotifyMembers}
        />
      </div>
    </Dialog>
  );
};
